/**
 * Explore default insight + timeframe, applied on casino entry (login reopen,
 * Select Casino, re-selecting the current casino) and by the "All" shortcut.
 *
 * Read from Mongo `applicationConfigurations` (same store and same login-session
 * cache as `egmDetailGraphDefaultTimeframe`, see lib/applicationConfig.ts):
 *
 * - `exploreDefaultInsight` (valueDataType `string`): an insight key from
 *   `allInsightKeys` (config/Insights), e.g. `return` = Hottest Slots (RTP).
 * - `exploreDefaultTimeframe` (valueDataType `timeframe`): casinos WITHOUT
 *   `insightInstance`. Any app timeframe key.
 * - `exploreDefaultTimeframeInsightCasino` (valueDataType `timeframe`): casinos
 *   WITH `insightInstance`. Only timeframes insight casinos offer
 *   (config/insightTimeframes: 1h, 4h, 8h, 24h, 7d, 30d — no sinceLastJackpot).
 *
 * Missing / invalid / not loaded / fetch failed → the Hottest (`return`, Desc) + 7d for every casino, including insight
 * casinos (decision Miller, Nov 3, 2026; insight casinos were 24h before).
 * The sort direction is not configurable (always Desc).
 */
import { OrderByEnum } from 'common/common';
import type { InsightValue, TimeFrameValue } from 'common/types';
import { allInsightKeys } from 'config/Insights';
import { isInsightTimeframeValue } from 'config/insightTimeframes';
import {
  EXPLORE_DEFAULT_INSIGHT_FALLBACK,
  EXPLORE_DEFAULT_TIMEFRAME_FALLBACK,
  EXPLORE_DEFAULT_TIMEFRAME_INSIGHT_CASINO_FALLBACK,
} from 'config/exploreDefaultFallbacks';
import { getConfig, TIMEFRAME_CONFIG_VALUES } from 'lib/applicationConfig';

export const EXPLORE_DEFAULT_INSIGHT_NAME = 'exploreDefaultInsight';
export const EXPLORE_DEFAULT_TIMEFRAME_NAME = 'exploreDefaultTimeframe';
export const EXPLORE_DEFAULT_TIMEFRAME_INSIGHT_CASINO_NAME = 'exploreDefaultTimeframeInsightCasino';

export {
  EXPLORE_DEFAULT_INSIGHT_FALLBACK,
  EXPLORE_DEFAULT_TIMEFRAME_FALLBACK,
  EXPLORE_DEFAULT_TIMEFRAME_INSIGHT_CASINO_FALLBACK,
};
export const EXPLORE_DEFAULT_ORDERBY = OrderByEnum.Desc;

const INSIGHT_KEY_SET = new Set<string>(allInsightKeys as string[]);
const TIMEFRAME_SET = new Set<string>(TIMEFRAME_CONFIG_VALUES);

export type ExploreDefaults = {
  insight: InsightValue;
  timeframe: TimeFrameValue;
  orderby: OrderByEnum;
};

const readString = (name: string): string | null => {
  const v = getConfig<unknown>(name, null);
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed ? trimmed : null;
};

/** True when `value` is an insight key the app knows. */
export const isExploreDefaultInsightValue = (value?: string | null): boolean =>
  !!value && INSIGHT_KEY_SET.has(value);

/** True when `value` is a timeframe this kind of casino offers. */
export const isExploreDefaultTimeframeValue = (
  value: string | null | undefined,
  isInsightCasino: boolean
): boolean => {
  if (!value) return false;
  return isInsightCasino ? isInsightTimeframeValue(value) : TIMEFRAME_SET.has(value);
};

/** Configured Explore default insight, else `return` (Hottest Slots). */
export function getExploreDefaultInsight(): InsightValue {
  const v = readString(EXPLORE_DEFAULT_INSIGHT_NAME);
  return isExploreDefaultInsightValue(v) ? (v as InsightValue) : EXPLORE_DEFAULT_INSIGHT_FALLBACK;
}

/**
 * Configured Explore default timeframe for this kind of casino.
 * Insight casinos: `exploreDefaultTimeframeInsightCasino`, else 7d.
 * Others: `exploreDefaultTimeframe`, else 7d.
 */
export function getExploreDefaultTimeframe(isInsightCasino: boolean): TimeFrameValue {
  const name = isInsightCasino
    ? EXPLORE_DEFAULT_TIMEFRAME_INSIGHT_CASINO_NAME
    : EXPLORE_DEFAULT_TIMEFRAME_NAME;
  const fallback = isInsightCasino
    ? EXPLORE_DEFAULT_TIMEFRAME_INSIGHT_CASINO_FALLBACK
    : EXPLORE_DEFAULT_TIMEFRAME_FALLBACK;
  const v = readString(name);
  return isExploreDefaultTimeframeValue(v, isInsightCasino) ? (v as TimeFrameValue) : fallback;
}

/** Insight + timeframe + sort applied on every Explore casino entry. */
export function getExploreDefaults(isInsightCasino: boolean): ExploreDefaults {
  return {
    insight: getExploreDefaultInsight(),
    timeframe: getExploreDefaultTimeframe(isInsightCasino),
    orderby: EXPLORE_DEFAULT_ORDERBY,
  };
}
